/**
 * The single self-refreshing session token, in `localStorage` (not
 * `sessionStorage`, not memory-only) so a tab opened by a link click,
 * ctrl/cmd-click, or `window.open` inherits the session immediately
 * (ADR-0018's multi-tab design). Signing out clears it here, which fires the
 * browser's own `storage` event in every OTHER open tab.
 */

import { appHref } from './globals.js';

const TOKEN_KEY = 'colplat.session.token';
const USER_ID_KEY = 'colplat.session.userId';

export interface StoredSession {
  token: string;
  userId: string;
}

export function getStoredSession(): StoredSession | undefined {
  const token = localStorage.getItem(TOKEN_KEY);
  const userId = localStorage.getItem(USER_ID_KEY);
  return token && userId ? { token, userId } : undefined;
}

export function storeSession(session: StoredSession): void {
  localStorage.setItem(TOKEN_KEY, session.token);
  localStorage.setItem(USER_ID_KEY, session.userId);
}

/** Fires `storage` in every other tab — they notice immediately, not just on their next reconnect. */
export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_ID_KEY);
}

export function onSignedOutElsewhere(fn: () => void): () => void {
  const handler = (event: StorageEvent): void => {
    if (event.key === TOKEN_KEY && event.newValue === null) fn();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/**
 * The one way out of a session the server has refused.
 *
 * Clearing comes first, and that ordering is the whole point. `/login`
 * resumes a stored session rather than asking again — someone who reloads
 * mid-task should land back in their task, not at a form — so a page that
 * redirects there while leaving a dead token in `localStorage` does not send
 * the user to the login page at all. It sends them to `/login`, which reads
 * the token, believes it, and sends them straight back:
 *
 *     /webledger/wallets -> /auth/me 401 -> /login -> /webledger/wallets -> ...
 *
 * as fast as the browser can manage it. Reported as "the page keeps blinking
 * and reloading", with four thousand console messages behind it, after the
 * platform database was replaced under a tab that still held a token for a
 * session that no longer existed.
 *
 * The launcher always did this correctly and the other pages open-coded it
 * without the clear, which is why landing on the apps list worked and
 * landing on a webledger page did not. One function now, so there is nothing
 * left to get half right.
 */
export function returnToLogin(): void {
  clearSession();
  window.location.assign(appHref('/login'));
}
